import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

const formatCurrency = (amountInCents, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format((amountInCents || 0) / 100);

const statusColor = (status) => {
  const key = String(status || "").toLowerCase();
  if (["open", "submitted"].includes(key)) return "warning";
  if (["processing"].includes(key)) return "info";
  if (["paid", "succeeded"].includes(key)) return "success";
  if (["failed", "void", "uncollectible"].includes(key)) return "error";
  return "default";
};

const statusLabel = (status) => String(status || "unknown").replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());

const Invoices = ({ embedded = false, loading, invoices, page, rowsPerPage, onPageChange, onRowsPerPageChange }) => {
  const pagedInvoices = invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openInvoicePdf = (invoice) => {
    const url = String(invoice?.invoice_pdf || invoice?.hosted_invoice_url || "").trim();
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const content = (
    <>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
        <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" }}>
          <ReceiptLongIcon fontSize="small" />
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 850, fontSize: 18 }}>Invoices</Typography>
          <Typography variant="body2" color="text.secondary">
            Review issued invoices and payment status.
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : invoices.length === 0 ? (
        <Box sx={{ flexGrow: 1, minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">No invoices found.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Invoice ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedInvoices.map((inv) => (
                <TableRow
                  key={inv.id}
                  hover
                  onClick={() => openInvoicePdf(inv)}
                  sx={{ cursor: inv.invoice_pdf || inv.hosted_invoice_url ? "pointer" : "default" }}
                >
                  <TableCell>{inv.created ? new Date(inv.created * 1000).toLocaleDateString() : "-"}</TableCell>
                  <TableCell>{inv.number || inv.id}</TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabel(inv.status)}
                      color={statusColor(inv.status)}
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(inv.amount_due || 0, inv.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {invoices.length > 0 && (
        <Box sx={{ mt: "auto", pt: 0.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={invoices.length}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage="Show"
          />
          <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }} />
        </Box>
      )}
    </>
  );

  if (embedded) {
    return <Box>{content}</Box>;
  }

  return (
    <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, mb: 2, p: 2 }}>
      {content}
    </Paper>
  );
};

export default Invoices;
